import { Component, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { filter, debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { Subject, of } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from '../../services/auth.service';
import { ArtistsService } from '../../services/artists.service';
import { SuggestArtistFlowService } from '../../services/suggest-artist-flow.service';
import { SearchService, SearchArtistItem, SearchSongItem, SearchAlbumItem, SearchResult, SearchSongArtistCredit } from '../../services/search.service';
import { SongDrawerService } from '../../services/song-drawer.service';
import { AlbumDrawerService } from '../../services/album-drawer.service';
import { Artist } from '../../models/artist.model';
import { ArtistListingsUiService } from '../../services/artist-listings-ui.service';
import { SongChartsUiService } from '../../services/song-charts-ui.service';
import { LayoutService } from '../../services/layout.service';
import { SearchBarComponent } from '../search-bar/search-bar.component';
import { ButtonComponent } from '../button/button.component';
import { formatNumber } from '../../utils/formatters';

export interface HeaderMainSection {
  title: string;
  route: string;
}

const DEFAULT_MAIN_SECTION: HeaderMainSection = {
  title: 'Artists',
  route: '/overview/artists',
};

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, RouterLinkActive, SearchBarComponent, ButtonComponent],
  templateUrl: './header.component.html'
})
export class HeaderComponent {
  authService = inject(AuthService);
  artistsService = inject(ArtistsService);
  searchService = inject(SearchService);
  songDrawerService = inject(SongDrawerService);
  albumDrawerService = inject(AlbumDrawerService);
  layoutService = inject(LayoutService);
  private suggestArtistFlow = inject(SuggestArtistFlowService);
  private artistListingsUi = inject(ArtistListingsUiService);
  private songChartsUi = inject(SongChartsUiService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  artists = this.artistsService.artists;

  searchQuery = signal('');
  isSearchActive = signal(false);
  isSearching = signal(false);
  searchResults = signal<SearchResult>({ artists: [], songs: [], albums: [] });
  selectedIndex = signal<number>(-1);
  private searchSubject = new Subject<string>();

  readonly flatResults = computed<Array<{ type: 'artist' | 'song' | 'album'; id: number; data: SearchArtistItem | SearchSongItem | SearchAlbumItem }>>(() => {
    const res = this.searchResults();
    const items: Array<{ type: 'artist' | 'song' | 'album'; id: number; data: SearchArtistItem | SearchSongItem | SearchAlbumItem }> = [];
    for (const a of res.artists) {
      items.push({ type: 'artist', id: a.id, data: a });
    }
    for (const s of res.songs) {
      items.push({ type: 'song', id: s.id, data: s });
    }
    for (const al of res.albums) {
      items.push({ type: 'album', id: al.id, data: al });
    }
    return items;
  });

  isMobileMenuOpen = signal(false);
  isStreamingOpen = signal<boolean>(false);
  readonly mobileBrand = 'HYPETRACK';
  currentPath = signal('');
  currentSource = signal<'overview' | 'charts'>('overview');
  lastMainSection = signal<HeaderMainSection>(this.loadStoredMainSection() ?? DEFAULT_MAIN_SECTION);

  headerTitle = computed(() => {
    this.currentPath();
    const info = this.getActiveRouteInfo();
    if (info.title && !info.isDetail) {
      return info.title;
    }
    return this.lastMainSection().title;
  });

  headerSubTitle = computed(() => {
    const path = this.currentPath();
    if (path.startsWith('/artist/')) {
      const segments = path.split('/').filter(Boolean);
      const idOrSlug = segments[1];
      if (idOrSlug) {
        const cached = this.artistsService.findArtistInCache(idOrSlug);
        if (cached?.name) {
          return cached.name;
        }
      }
      return this.artistsService.currentArtistName();
    }
    return null;
  });

  isHeaderArtistVerified = computed(() => {
    const path = this.currentPath();
    if (path.startsWith('/artist/')) {
      const segments = path.split('/').filter(Boolean);
      const idOrSlug = segments[1];
      if (idOrSlug) {
        const cached = this.artistsService.findArtistInCache(idOrSlug);
        if (cached !== undefined) {
          return !!cached.verified;
        }
      }
      return this.artistsService.currentArtistVerified();
    }
    return false;
  });

  headerRoute = computed(() => {
    const path = this.currentPath();
    const info = this.getActiveRouteInfo();
    if (info.title && !info.isDetail) {
      return path;
    }
    return this.lastMainSection().route;
  });

  constructor() {
    if (!this.artists().length) {
      void this.artistsService.loadArtists();
    }
    this.updateHeaderContext(this.router.url);
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.updateHeaderContext(this.router.url);
        this.closeMobileMenu();
      });

    this.searchSubject
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        tap((q) => {
          if (q.trim().length > 0) {
            this.isSearching.set(true);
          } else {
            this.isSearching.set(false);
            this.searchResults.set({ artists: [], songs: [], albums: [] });
          }
        }),
        switchMap((q) => {
          if (!q.trim()) {
            return of({ artists: [], songs: [], albums: [] });
          }
          return this.searchService.search(q, 6);
        }),
        takeUntilDestroyed()
      )
      .subscribe((results) => {
        this.searchResults.set(results);
        this.isSearching.set(false);
        this.selectedIndex.set(-1);
      });
  }

  toggleMenu(): void {
    this.isMobileMenuOpen.update((open) => {
      const next = !open;
      if (next) {
        this.isSearchActive.set(false);
      }
      this.setMobileMenuScrollLock(next);
      return next;
    });
  }

  closeMobileMenu(): void {
    this.isMobileMenuOpen.set(false);
    this.setMobileMenuScrollLock(false);
  }

  onMobileMenuArtistsClick(): void {
    this.artistListingsUi.requestOverviewReset();
    this.closeMobileMenu();
  }

  onMobileLogout(): void {
    this.authService.logout();
    this.closeMobileMenu();
  }

  onMobileMenuSongsClick(): void {
    this.songChartsUi.requestChartsReset();
    this.closeMobileMenu();
  }

  toggleStreamingMenu(): void {
    this.isStreamingOpen.update((open) => !open);
  }

  isStreamingRouteActive(): boolean {
    const url = this.router.url;
    return (
      url.startsWith('/charts/billboard') ||
      url.startsWith('/charts/spotify') ||
      url.startsWith('/charts/shazam') ||
      url.startsWith('/charts/apple-music')
    );
  }

  onSearchResultClick(): void {
    this.closeSearch();
    this.closeMobileMenu();
  }

  closeDrawersIfOpen(): void {
    if (this.songDrawerService.activeSongId() !== null) {
      this.songDrawerService.close();
    }
    if (this.albumDrawerService.activeAlbumId() !== null) {
      this.albumDrawerService.close();
    }
  }

  onSearchBarClick(): void {
    this.closeDrawersIfOpen();
  }

  onSearchInput(value: string): void {
    this.closeDrawersIfOpen();
    this.searchQuery.set(value);
    this.selectedIndex.set(-1);
    if (value.trim().length > 0) {
      this.isSearchActive.set(true);
    }
    this.searchSubject.next(value);
  }

  onSearchClear(): void {
    this.searchQuery.set('');
    this.searchResults.set({ artists: [], songs: [], albums: [] });
    this.selectedIndex.set(-1);
    this.isSearchActive.set(false);
    this.searchSubject.next('');
  }

  private blurTimer: any = null;

  private clearBlurTimer(): void {
    if (this.blurTimer) {
      clearTimeout(this.blurTimer);
      this.blurTimer = null;
    }
  }

  closeSearchImmediate(): void {
    this.clearBlurTimer();
    this.isSearchActive.set(false);
    this.selectedIndex.set(-1);
    this.isKeyboardNavigating = false;
    this.lastMouseX = -1;
    this.lastMouseY = -1;
  }

  onArtistClick(artistId: number): void {
    this.closeSearchImmediate();
    this.closeMobileMenu();
    void this.router.navigate(['/artist', artistId]);
  }

  onSongClick(songId: number): void {
    this.closeSearchImmediate();
    this.closeMobileMenu();
    this.songDrawerService.open(songId);
  }

  onAlbumClick(albumId: number): void {
    this.closeSearchImmediate();
    this.closeMobileMenu();
    this.albumDrawerService.open(albumId);
  }

  getArtistNavIndex(i: number): number {
    return i;
  }

  getSongNavIndex(i: number): number {
    return this.searchResults().artists.length + i;
  }

  getAlbumNavIndex(i: number): number {
    return this.searchResults().artists.length + this.searchResults().songs.length + i;
  }

  formatStreams(value: number | null | undefined): string {
    return formatNumber(value);
  }

  getSongArtistsLabel(artists: SearchSongArtistCredit[]): string {
    if (!artists || !artists.length) return '';
    return artists.map((a) => a.name).join(', ');
  }

  getAlbumYear(releaseDate: string | null | undefined): string {
    if (!releaseDate) return '';
    const d = new Date(releaseDate);
    return isNaN(d.getFullYear()) ? '' : String(d.getFullYear());
  }

  private isKeyboardNavigating = false;
  private lastMouseX = -1;
  private lastMouseY = -1;

  onItemMouseMove(index: number, event: MouseEvent): void {
    // If the mouse coordinates haven't changed at all, the event was generated by scrolling under the cursor. Ignore!
    if (this.lastMouseX === event.clientX && this.lastMouseY === event.clientY) {
      return;
    }

    if (this.isKeyboardNavigating) {
      if (this.lastMouseX === -1) {
        this.lastMouseX = event.clientX;
        this.lastMouseY = event.clientY;
        return;
      }
      // User physically moved their mouse, exit keyboard navigation mode
      this.isKeyboardNavigating = false;
    }

    this.lastMouseX = event.clientX;
    this.lastMouseY = event.clientY;
    this.selectedIndex.set(index);
  }

  openSearch() {
    this.closeDrawersIfOpen();
    this.clearBlurTimer();
    this.isSearchActive.set(true);
    this.selectedIndex.set(-1);
    this.isKeyboardNavigating = false;
    this.lastMouseX = -1;
    this.lastMouseY = -1;
    if (this.searchQuery().trim() && this.searchResults().artists.length === 0 && this.searchResults().songs.length === 0 && this.searchResults().albums.length === 0) {
      this.searchSubject.next(this.searchQuery());
    }
  }

  closeSearch() {
    this.clearBlurTimer();
    this.blurTimer = setTimeout(() => {
      this.closeSearchImmediate();
    }, 350);
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      this.isKeyboardNavigating = true;
    }

    if (!this.isSearchActive() || this.searchQuery().trim().length === 0) {
      if (event.key === 'ArrowDown' && this.searchQuery().trim().length > 0) {
        this.isSearchActive.set(true);
      } else {
        return;
      }
    }

    const results = this.flatResults();
    const resultsCount = results.length;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (resultsCount > 0) {
        const nextIndex = (this.selectedIndex() + 1) % resultsCount;
        this.selectedIndex.set(nextIndex);
        this.scrollToIndex(nextIndex);
      }
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (resultsCount > 0) {
        const prevIndex = this.selectedIndex() <= 0 ? resultsCount - 1 : this.selectedIndex() - 1;
        this.selectedIndex.set(prevIndex);
        this.scrollToIndex(prevIndex);
      }
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (resultsCount > 0) {
        const targetIndex = this.selectedIndex() >= 0 ? this.selectedIndex() : 0;
        const selectedItem = results[targetIndex];
        if (selectedItem) {
          if (selectedItem.type === 'artist') {
            this.onArtistClick(selectedItem.id);
          } else if (selectedItem.type === 'song') {
            this.onSongClick(selectedItem.id);
          } else if (selectedItem.type === 'album') {
            this.onAlbumClick(selectedItem.id);
          }
          (event.target as HTMLElement)?.blur();
        }
      } else {
        this.openSuggestArtist();
        (event.target as HTMLElement)?.blur();
      }
    } else if (event.key === 'Escape') {
      this.closeSearchImmediate();
      (event.target as HTMLElement)?.blur();
    }
  }

  private scrollToIndex(index: number): void {
    setTimeout(() => {
      const activeEl = document.querySelector(`[data-search-index="${index}"]`);
      if (activeEl) {
        activeEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }, 0);
  }

  openSuggestArtist() {
    this.closeSearchImmediate();
    this.searchQuery.set('');
    this.searchResults.set({ artists: [], songs: [], albums: [] });
    this.suggestArtistFlow.open();
  }

  /** When already on listing routes, the title link may not navigate; reset UI like a fresh visit. */
  onHeaderTitleClick(): void {
    const title = this.headerTitle();
    const p = this.currentPath();
    if (title === 'Artists') {
      if (p === '/' || p === '/artists' || p === '/overview/artists') {
        this.artistListingsUi.requestOverviewReset();
      }
      return;
    }
    if (title === 'IFPI Charts' && (p.startsWith('/songs') || p.startsWith('/charts/ifpi'))) {
      this.songChartsUi.requestChartsReset();
    }
  }

  private setMobileMenuScrollLock(locked: boolean): void {
    if (typeof document === 'undefined') return;
    document.body.style.overflow = locked ? 'hidden' : '';
  }

  private updateHeaderContext(url: string): void {
    const cleanUrl = url.split('?')[0] || '/';
    this.currentPath.set(cleanUrl);

    const info = this.getActiveRouteInfo();
    if (info.title && !info.isDetail) {
      const section: HeaderMainSection = {
        title: info.title,
        route: cleanUrl,
      };
      this.lastMainSection.set(section);
      this.saveStoredMainSection(section);
    }

    const tree = this.router.parseUrl(url);
    const source = tree.queryParams['source'];
    this.currentSource.set(source === 'charts' ? 'charts' : 'overview');
  }

  private getActiveRouteInfo(): { title?: string; isDetail: boolean } {
    let route = this.activatedRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }
    const snapshot = route.snapshot;
    const isDetail = !!snapshot.data?.['isDetail'];
    const title = snapshot.title ?? snapshot.data?.['title'] ?? (typeof snapshot.routeConfig?.title === 'string' ? snapshot.routeConfig.title : undefined);

    return {
      title: title ?? undefined,
      isDetail,
    };
  }

  private loadStoredMainSection(): HeaderMainSection | null {
    if (typeof sessionStorage === 'undefined') return null;
    try {
      const data = sessionStorage.getItem('hype_last_main_section');
      return data ? JSON.parse(data) : null;
    } catch {
      return null;
    }
  }

  private saveStoredMainSection(section: HeaderMainSection): void {
    if (typeof sessionStorage === 'undefined') return;
    try {
      sessionStorage.setItem('hype_last_main_section', JSON.stringify(section));
    } catch {
      // Ignore storage errors
    }
  }
}